import { PatternPlayer } from "../clip/patternPlayer";
import type { ClipIR } from "../clip/types";
import { clipOrderSteps, secondsPerStep } from "../clip/types";
import type { FxNode, InstrumentSlot, KernelMessage } from "../types";
import type { AudioEngine } from "./engine";
import { createEngineSchedulerHost, type SchedulerHost } from "./schedulerHost";
import { SchedulerProxy } from "./schedulerProxy";
import { WorkletMusicProxy } from "./workletMusicProxy";

export const SFX_CHANNEL_COUNT = 4;

export type PlayMusicOptions = {
  /** Override clip.loop; default keeps the registered clip's loop flag. */
  loop?: boolean;
  /** Absolute step index along `order` (sum of pattern lengths). Default 0. */
  startStep?: number;
  /**
   * Seconds to crossfade into the new clip.
   * Stub until P3b bus gains — currently hard-cuts.
   */
  crossfade?: number;
};

export type StopMusicOptions = {
  /**
   * Seconds to fade out.
   * Stub until P3b bus gains — currently hard-cuts.
   */
  fade?: number;
};

export type AudioSessionOptions = {
  /** Music playhead callback (SFX do not emit playhead). */
  onPlayhead?: (info: { patternId: string; row: number } | undefined) => void;
  lookahead?: number;
  intervalMs?: number;
  /**
   * `worklet` — PatternPlayer inside AudioWorklet (preferred; sample clock).
   * `worker` — Dedicated Worker PatternPlayer (legacy soft-RT hop).
   * `main` — in-process PatternPlayer (tests / fallback).
   */
  scheduler?: "main" | "worker" | "worklet";
  /** Required when `scheduler: "worker"`. */
  schedulerWorkerUrl?: string;
  /** Called when the worker scheduler fails and main-thread fallback engages. */
  onSchedulerFallback?: (reason: string) => void;
  /**
   * Observe timed music note traffic (worklet / worker / main music paths).
   * Used by External MIDI instrument sink.
   */
  onKernelMessage?: (message: KernelMessage) => void;
};

type SfxChannel = {
  player: PatternPlayer;
  /** Audio time until which this channel is considered occupied (one-shot length). */
  busyUntil: number;
  startedAt: number;
};

/** Shared music-control surface for main PatternPlayer or SchedulerProxy. */
type MusicBackend = {
  isPlaying(): boolean;
  getMuted(): boolean[];
  getSolo(): boolean[];
  setClip(clip: ClipIR): void;
  start(clip: ClipIR, options?: { startStep?: number }): void;
  stop(): void;
  setTrackMute(slot: InstrumentSlot, muted: boolean): void;
  setTrackSolo(slot: InstrumentSlot, solo: boolean): void;
  setMuteSolo(state: { muted?: boolean[]; solo?: boolean[] }): void;
  setOpenHoldDefaultSlots(flags: boolean[]): void;
  dispose(): void;
};

/**
 * Host channel API: named clip registry, 1 music player, 4 SFX one-shot slots.
 * Shares the engine's 4 mono voices (newest note wins per slot).
 */
export class AudioSession {
  private readonly clips = new Map<string, ClipIR>();
  private music: MusicBackend;
  private usingWorkerMusic = false;
  private readonly sfx: SfxChannel[];
  private musicName: string | undefined;
  private transportUsers = 0;
  private readonly lookahead: number;
  private readonly intervalMs: number;
  private openHoldDefaultSlots: boolean[] = [];

  constructor(
    private readonly engine: AudioEngine,
    private readonly options: AudioSessionOptions = {},
  ) {
    this.lookahead = options.lookahead ?? 0.5;
    this.intervalMs = options.intervalMs ?? 25;
    const shared = {
      lookahead: this.lookahead,
      intervalMs: this.intervalMs,
    };

    this.music = this.createMusicBackend(shared);

    this.sfx = Array.from({ length: SFX_CHANNEL_COUNT }, () => {
      const channel: SfxChannel = {
        player: undefined as unknown as PatternPlayer,
        busyUntil: 0,
        startedAt: 0,
      };
      channel.player = new PatternPlayer(engine, {
        ...shared,
        manageTransport: false,
        syncBpm: false,
        advanceClock: false,
        onEnded: () => this.releaseTransport(),
      });
      return channel;
    });
  }

  registerSound(name: string, clip: ClipIR): void {
    this.clips.set(name, structuredClone(clip));
  }

  updateSound(name: string, clip: ClipIR): void {
    this.clips.set(name, structuredClone(clip));
    if (this.musicName === name && this.music.isPlaying()) {
      this.music.setClip(this.clips.get(name)!);
    }
  }

  getClip(name: string): ClipIR | undefined {
    const clip = this.clips.get(name);
    return clip ? structuredClone(clip) : undefined;
  }

  listSounds(): string[] {
    return [...this.clips.keys()];
  }

  /**
   * Play a registered clip once on an SFX channel.
   * @returns Channel index used (0..3).
   */
  playSound(name: string, channel?: number): number {
    const clip = this.requireClip(name);
    const oneShot: ClipIR = { ...structuredClone(clip), loop: false };
    const index = this.allocateSfxChannel(channel);
    const slot = this.sfx[index]!;
    if (this.sfxOccupied(slot)) {
      slot.player.stop();
      slot.busyUntil = 0;
    }
    this.acquireTransport();
    const now = this.engine.getCurrentTime();
    slot.startedAt = now;
    // Hold the channel for the clip's audible length even if the scheduler
    // finishes the one-shot in the first lookahead pass.
    slot.busyUntil = now + clipOrderSteps(oneShot) * secondsPerStep(oneShot) + 0.05;
    slot.player.start(oneShot);
    return index;
  }

  playMusic(name: string, options: PlayMusicOptions = {}): void {
    const clip = this.requireClip(name);
    // crossfade is accepted but ignored until P3b bus gains
    void options.crossfade;

    if (this.music.isPlaying()) {
      this.music.stop();
    }

    const next: ClipIR = {
      ...structuredClone(clip),
      loop: options.loop ?? clip.loop,
    };
    this.musicName = name;
    this.acquireTransport();
    this.music.start(next, { startStep: options.startStep });
  }

  stopSound(channel?: number): void {
    if (channel === undefined) {
      for (const slot of this.sfx) {
        if (this.sfxOccupied(slot)) {
          slot.player.stop();
        }
        slot.busyUntil = 0;
      }
      this.engine.transport.allNotesOff();
      return;
    }
    const slot = this.sfx[channel];
    if (!slot) {
      return;
    }
    if (this.sfxOccupied(slot)) {
      slot.player.stop();
    }
    slot.busyUntil = 0;
  }

  stopMusic(_options: StopMusicOptions = {}): void {
    // fade is accepted but ignored until P3b bus gains
    void _options.fade;
    if (this.music.isPlaying()) {
      this.music.stop();
    }
    this.musicName = undefined;
    this.engine.transport.allNotesOff();
  }

  setBpm(bpm: number): void {
    this.engine.transport.setBpm(bpm);
    if (this.musicName) {
      const clip = this.clips.get(this.musicName);
      if (clip) {
        clip.bpm = this.engine.transport.bpm;
        if (this.music.isPlaying()) {
          this.music.setClip(clip);
        }
      }
    }
  }

  setFxStack(effects: FxNode[]): void {
    this.engine.transport.setFxStack(effects);
  }

  setFx(index: number, patch: Partial<FxNode>): void {
    this.engine.transport.setFx(index, patch);
  }

  setFxBypass(bypass: boolean): void {
    this.engine.transport.setFxBypass(bypass);
  }

  clearFx(): void {
    this.engine.transport.clearFx();
  }

  setSlotFxStack(slot: InstrumentSlot, effects: FxNode[]): void {
    this.engine.transport.setSlotFxStack(slot, effects);
  }

  setSlotFxStacks(stacks: FxNode[][]): void {
    this.engine.transport.setSlotFxStacks(stacks);
  }

  clearSlotFx(slot?: InstrumentSlot): void {
    this.engine.transport.clearSlotFx(slot);
  }

  isMusicPlaying(): boolean {
    return this.music.isPlaying();
  }

  getMusicName(): string | undefined {
    return this.musicName;
  }

  getMusicMuted(): boolean[] {
    return this.music.getMuted();
  }

  getMusicSolo(): boolean[] {
    return this.music.getSolo();
  }

  setMusicTrackMute(slot: InstrumentSlot, muted: boolean): void {
    this.music.setTrackMute(slot, muted);
  }

  setMusicTrackSolo(slot: InstrumentSlot, solo: boolean): void {
    this.music.setTrackSolo(slot, solo);
  }

  setMusicMuteSolo(state: { muted?: boolean[]; solo?: boolean[] }): void {
    this.music.setMuteSolo(state);
  }

  /**
   * Opt-in per-slot open-hold gates (like C--). Explicit `Cxx` / `---` still cut.
   */
  setOpenHoldDefaultSlots(flags: boolean[]): void {
    this.openHoldDefaultSlots = flags.slice();
    this.music.setOpenHoldDefaultSlots(flags);
  }

  /** Whether an SFX channel is occupied (scheduling or within clip duration). */
  isSfxBusy(channel: number): boolean {
    const slot = this.sfx[channel];
    return slot ? this.sfxOccupied(slot) : false;
  }

  dispose(): void {
    this.music.dispose();
    for (const slot of this.sfx) {
      slot.player.dispose();
      slot.busyUntil = 0;
    }
    this.transportUsers = 0;
    this.engine.transport.stop();
    this.engine.transport.allNotesOff();
    this.musicName = undefined;
  }

  private createMusicBackend(shared: {
    lookahead: number;
    intervalMs: number;
  }): MusicBackend {
    if (this.options.scheduler === "worklet" && this.engine.mode === "worklet") {
      return new WorkletMusicProxy({
        engine: this.engine,
        lookahead: shared.lookahead,
        onPlayhead: info => this.options.onPlayhead?.(info),
        onEnded: () => this.releaseTransport(),
        onKernelMessage: message => this.options.onKernelMessage?.(message),
      });
    }

    const wantWorker =
      this.options.scheduler === "worker"
      && typeof this.options.schedulerWorkerUrl === "string"
      && this.options.schedulerWorkerUrl.length > 0
      && typeof Worker !== "undefined";

    if (wantWorker) {
      const proxy = new SchedulerProxy({
        workerUrl: this.options.schedulerWorkerUrl!,
        engine: this.engine,
        lookahead: shared.lookahead,
        intervalMs: shared.intervalMs,
        onPlayhead: info => this.options.onPlayhead?.(info),
        onEnded: () => this.releaseTransport(),
        onKernelMessage: message => this.options.onKernelMessage?.(message),
        onError: reason => {
          this.options.onSchedulerFallback?.(reason);
          this.fallbackMusicToMain(shared, reason);
        },
      });
      if (!proxy.hasFailed()) {
        this.usingWorkerMusic = true;
        return proxy;
      }
      proxy.dispose();
      this.options.onSchedulerFallback?.("Worker failed to construct");
    }

    this.usingWorkerMusic = false;
    return this.createMainMusicPlayer(shared);
  }

  private createMainMusicPlayer(shared: {
    lookahead: number;
    intervalMs: number;
  }): PatternPlayer {
    return new PatternPlayer(this.createObservingHost(), {
      ...shared,
      manageTransport: false,
      syncBpm: true,
      advanceClock: true,
      onPlayhead: info => this.options.onPlayhead?.(info),
      onEnded: () => this.releaseTransport(),
    });
  }

  /** Main-thread host that mirrors note traffic to `onKernelMessage`. */
  private createObservingHost(): SchedulerHost {
    const base = createEngineSchedulerHost(this.engine);
    const emit = this.options.onKernelMessage;
    if (!emit) {
      return base;
    }
    return {
      get mode() {
        return base.mode;
      },
      getCurrentTime: () => base.getCurrentTime(),
      advanceTime: seconds => base.advanceTime?.(seconds),
      setBpm: bpm => base.setBpm(bpm),
      play: () => base.play(),
      stop: () => base.stop(),
      allNotesOff: () => {
        emit({ type: "allNotesOff" });
        base.allNotesOff();
      },
      cancelPending: afterTime => base.cancelPending(afterTime),
      noteOn: (slot, note, velocity = 1, time = 0, noteFx) => {
        emit({ type: "noteOn", slot, note, velocity, time, noteFx });
        base.noteOn(slot, note, velocity, time, noteFx);
      },
      noteOff: (slot, note, time = 0) => {
        emit({ type: "noteOff", slot, note, time });
        base.noteOff(slot, note, time);
      },
      setNoteFx: (slot, noteFx, time = 0, velocity) => {
        emit({ type: "setNoteFx", slot, noteFx, time, velocity });
        base.setNoteFx(slot, noteFx, time, velocity);
      },
    };
  }

  /** Swap a failed/errored worker proxy for an in-process PatternPlayer. */
  private fallbackMusicToMain(
    shared: { lookahead: number; intervalMs: number },
    _reason: string,
  ): void {
    if (!this.usingWorkerMusic) {
      return;
    }
    this.usingWorkerMusic = false;
    const wasPlaying = this.music.isPlaying();
    const muted = this.music.getMuted();
    const solo = this.music.getSolo();
    const name = this.musicName;
    try {
      this.music.dispose();
    } catch {
      // ignore
    }
    const main = this.createMainMusicPlayer(shared);
    main.setMuteSolo({ muted, solo });
    main.setOpenHoldDefaultSlots(this.openHoldDefaultSlots);
    this.music = main;
    if (wasPlaying && name) {
      const clip = this.clips.get(name);
      if (clip) {
        main.start({ ...structuredClone(clip), loop: clip.loop });
      }
    }
  }

  private requireClip(name: string): ClipIR {
    const clip = this.clips.get(name);
    if (!clip) {
      throw new Error(`Unknown sound "${name}" — call registerSound first`);
    }
    return clip;
  }

  private sfxOccupied(slot: SfxChannel): boolean {
    return slot.player.isPlaying() || this.engine.getCurrentTime() < slot.busyUntil;
  }

  private allocateSfxChannel(requested?: number): number {
    if (requested !== undefined) {
      if (requested < 0 || requested >= SFX_CHANNEL_COUNT) {
        throw new Error(`SFX channel must be 0..${SFX_CHANNEL_COUNT - 1}`);
      }
      return requested;
    }
    const free = this.sfx.findIndex(slot => !this.sfxOccupied(slot));
    if (free >= 0) {
      return free;
    }
    let oldest = 0;
    for (let i = 1; i < this.sfx.length; i++) {
      if (this.sfx[i]!.startedAt < this.sfx[oldest]!.startedAt) {
        oldest = i;
      }
    }
    return oldest;
  }

  private acquireTransport(): void {
    if (this.transportUsers === 0) {
      this.engine.transport.play();
    }
    this.transportUsers += 1;
  }

  private releaseTransport(): void {
    this.transportUsers = Math.max(0, this.transportUsers - 1);
    if (this.transportUsers === 0) {
      this.engine.transport.stop();
    }
  }
}
